import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, CalendarX2, Video, Phone, MessageSquare, IndianRupee } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { fmtSlotTime, slotDate } from "@lawyer/components/mobile/statusUtils";

const TYPE_ICON = { video: Video, audio: Phone, chat: MessageSquare };
const MAX_ROWS = 4;
const LIVE = ["live", "waiting_for_user", "waiting_for_lawyer"];

function statusChip(c, now) {
  if (LIVE.includes(c.status)) return { label: "Live now", cls: "bg-rose-600 text-white" };
  if (c.status === "completed") return { label: "Completed", cls: "bg-slate-100 text-slate-600" };
  if (c.status === "no_show") return { label: "Client no-show", cls: "bg-slate-100 text-slate-600" };
  const start = slotDate(c);
  if (start) {
    const mins = Math.round((start.getTime() - now) / 60000);
    if (mins > 0 && mins <= 120) return { label: `Starts in ${mins >= 60 ? `${Math.floor(mins / 60)} hr` : `${mins} min`}`, cls: "bg-emerald-100 text-emerald-800" };
    if (mins <= 0) return { label: "Slot time reached", cls: "bg-amber-100 text-amber-800" };
  }
  return { label: c.status === "pending" ? "Pending" : "Upcoming", cls: "bg-slate-100 text-slate-600" };
}

// `wide` = desktop rows: status chip + "View Details" button on the right, more rows.
export function MobileTodayList({ agenda = [], state, nextAvailableText, wide = false, title = "Today's Consultations", emptyTitle }) {
  const now = Date.now();
  const rows = agenda.slice(0, wide ? 6 : MAX_ROWS);
  const limit = wide ? 6 : MAX_ROWS;
  return (
    <section data-testid="m-today">
      <div className="flex items-center justify-between mb-2">
        <h2 className={`${wide ? "text-lg" : "text-base"} font-extrabold text-[#0A2540]`} style={{ fontFamily: "Poppins" }}>{title}</h2>
        <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1" data-testid="m-see-all-consultations">
          See all <ArrowRight size={12} />
        </Link>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="m-today-empty">
          <CalendarX2 size={26} className="mx-auto text-slate-300" />
          <p className="mt-2 text-sm font-bold text-[#0A2540]">{emptyTitle || (wide ? "No consultations scheduled" : "No consultations today")}</p>
          <p className="mt-1 text-xs text-slate-500">
            {state === "vacation" ? (wide && nextAvailableText ? `You are on vacation. Your next availability is ${nextAvailableText}.` : "You are on vacation. Your profile remains visible, but new bookings are not allowed until you end vacation mode.")
              : state === "offline" ? "Stay online to get new bookings."
              : state === "busy" ? "Your schedule is clear after this consultation." : "Stay online for instant bookings."}
          </p>
        </div>
      ) : (
        <ul className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
          {rows.map((c) => {
            const Icon = TYPE_ICON[c.consultation_type] || Video;
            const chip = statusChip(c, now);
            const isLive = LIVE.includes(c.status);
            const to = isLive ? `${LAWYER_BASE}/consultations/${c.id}/room` : `${LAWYER_BASE}/consultations?bucket=${c.status === "completed" ? "completed" : "upcoming"}`;
            return (
              <li key={c.id}>
                <Link to={to} className={`flex items-center gap-3 ${wide ? "px-4 py-3.5 hover:bg-slate-50" : "px-3 py-3 active:bg-slate-50"} ${isLive ? "bg-rose-50/60" : ""}`} data-testid={`m-agenda-${c.id}`}>
                  <div className="w-16 shrink-0">
                    <p className="text-sm font-extrabold text-[#0A2540] leading-tight">{fmtSlotTime(c.slot_time)}</p>
                    <p className="text-[10px] text-slate-400">Today</p>
                  </div>
                  <div className={`h-9 w-9 shrink-0 rounded-full grid place-items-center ${isLive ? "bg-rose-600 text-white" : "bg-[#0A2540] text-[#D4AF37]"}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0A2540] truncate">{c.user_name || "Client"}</p>
                    <p className="text-[11px] text-slate-500 capitalize truncate">
                      {c.consultation_type}{c.duration_minutes ? ` · ${c.duration_minutes} min` : ""}{c.query ? ` · ${c.query}` : ""}
                    </p>
                    {!wide && <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${chip.cls}`}>{chip.label}</span>}
                  </div>
                  {wide ? (
                    <>
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 ${chip.cls}`}>{chip.label}</span>
                      <span className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-bold text-[#0A2540] inline-flex items-center shrink-0">{isLive ? "Join live" : "View Details"}</span>
                    </>
                  ) : <ChevronRight size={16} className="text-slate-300 shrink-0" />}
                </Link>
              </li>
            );
          })}
          {agenda.length > limit && (
            <li>
              <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} className="block px-3 py-2.5 text-center text-xs font-bold text-[#0A2540]" data-testid="m-today-more">
                +{agenda.length - limit} more today — See all
              </Link>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

// This month's earned amount, derived client-side from the existing earnings ledger (IST month).
// Returns null while loading / on failure so callers can fall back to the all-time total.
export const rupees = (paise) => Math.round((paise || 0) / 100).toLocaleString("en-IN");

export function MobileEarningsCard({ monthPaise = null, fallbackPaise = 0 }) {
  const amount = rupees(monthPaise ?? fallbackPaise);
  const title = monthPaise == null ? "Total earned" : "This Month";
  return (
    <Link to={`${LAWYER_BASE}/earnings`} className="flex items-center gap-3 rounded-2xl border border-[#F1E3B5] bg-[#FBF4DF] p-4" data-testid="m-earnings-card">
      <div className="h-10 w-10 rounded-xl bg-[#0A2540] text-[#D4AF37] grid place-items-center shrink-0"><IndianRupee size={18} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-slate-500">{title}</p>
        <p className="text-2xl font-extrabold text-[#0A2540] leading-tight" style={{ fontFamily: "Poppins" }} data-testid="m-earnings-amount">₹{amount}</p>
      </div>
      <span className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1">See Earnings <ArrowRight size={12} /></span>
    </Link>
  );
}
